<template>
  <div>
    <AppSidebar />
    <div class="wrapper d-flex flex-column min-vh-100 bg-light">
      <AppHeader />
      <div class="body flex-grow-1 px-3">
        <CContainer lg>
          <router-view />
        </CContainer>
      </div>
      <AppFooter />
    </div>

    <!-- Optional Network Selection Modal (Custom Premium Implementation) -->
    <transition name="custom-modal-fade">
      <div v-if="modalVisible" class="custom-modal-overlay" @click.self="closeModal">
        <div class="custom-modal-card">
          <div class="custom-modal-header">
            <h5 class="custom-modal-title">{{ $t('networkSelection.title') }}</h5>
            <div class="d-flex align-items-center">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary custom-lang-toggle me-3 d-flex align-items-center"
                @click="toggleLanguage"
              >
                <i class="pi pi-globe me-1"></i>
                <span>{{ currentLocale === 'fa' ? 'English' : 'فارسی' }}</span>
              </button>
              <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
            </div>
          </div>
          <div class="custom-modal-body">
            <div class="mb-3">
              <p class="text-secondary">{{ $t('networkSelection.info') }}</p>
            </div>
            <div class="mb-3">
              <label for="network-select" class="form-label fw-bold text-dark">{{ $t('networkSelection.selectLabel') }}</label>
              <select
                id="network-select"
                class="form-select custom-select"
                v-model="selectedNetworkUsername"
              >
                <option value="" disabled>{{ $t('networkSelection.placeholder') }}</option>
                <option
                  v-for="net in availableNetworks"
                  :key="net.organization ? net.organization.username : ''"
                  :value="net.organization ? net.organization.username : ''"
                >
                  {{ net.organization ? (net.organization.title || net.organization.username) : '' }}
                </option>
              </select>
            </div>
            <div class="alert alert-warning text-sm p-3 d-flex align-items-start rounded-3 animate-pulse" role="alert">
              <i class="pi pi-exclamation-triangle me-2 mt-1" style="font-size: 1.1rem; color: #b27b00;"></i>
              <div class="warning-text" style="color: #664d03; font-size: 0.875rem; line-height: 1.4;">
                {{ $t('networkSelection.consequence') }}
              </div>
            </div>
          </div>
          <div class="custom-modal-footer">
            <button type="button" class="btn btn-secondary custom-btn-skip" @click="closeModal">
              {{ $t('networkSelection.skipButton') }}
            </button>
            <button
              type="button"
              class="btn btn-primary custom-btn-select"
              :disabled="!selectedNetworkUsername"
              @click="selectNetwork"
            >
              {{ $t('networkSelection.selectButton') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { CContainer } from '@coreui/vue'
import { setLocale } from '@/i18n'
import AppFooter from '@/components/AppFooter.vue'
import AppHeader from '@/components/AppHeader.vue'
import AppSidebar from '@/components/AppSidebar.vue'

export default {
  name: 'DefaultLayout',
  components: {
    AppFooter,
    AppHeader,
    AppSidebar,
    CContainer,
  },
  data() {
    return {
      skipped: false,
      selectedNetworkUsername: '',
      modalVisible: false,
    }
  },
  computed: {
    availableNetworks() {
      return this.$store.state.all_networks || []
    },
    currentLocale() {
      return this.$i18n.locale
    },
    hasSelectedValidNetwork() {
      const currentNetworkId = this.$store.state.network_id
      if (!currentNetworkId) return false
      return this.availableNetworks.some(
        net => net.organization && net.organization.username === currentNetworkId
      )
    },
    showNetworkModal() {
      const token = this.$store.state.access_token
      const networks = this.availableNetworks
      return !!token && networks.length > 1 && !this.hasSelectedValidNetwork && !this.skipped
    }
  },
  watch: {
    showNetworkModal: {
      immediate: true,
      handler(newVal) {
        this.modalVisible = newVal
      }
    },
    modalVisible(newVal) {
      document.body.style.overflow = newVal ? 'hidden' : ''
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.modalVisible = this.showNetworkModal
    })
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
  methods: {
    toggleLanguage() {
      const nextLocale = this.currentLocale === 'fa' ? 'en' : 'fa'
      setLocale(nextLocale)
    },
    closeModal() {
      this.skipped = true
      this.modalVisible = false
    },
    async selectNetwork() {
      if (!this.selectedNetworkUsername) return

      const selected = this.availableNetworks.find(
        net => net.organization && net.organization.username === this.selectedNetworkUsername
      )
      if (selected) {
        try {
          this.$store.commit('setAccessToken', selected.token.accessToken)
          this.$store.commit('setRefreshToken', selected.token.refreshToken)
          this.$store.commit('setNetworkId', selected.organization.username)
          window.location.reload()
        } catch (err) {
          console.error(err)
          this.$toast.add({
            severity: 'error',
            summary: this.$t('common.error'),
            detail: err.message || 'Failed to select network',
            life: 6000
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.custom-modal-card {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 520px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(0);
}

.custom-modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-modal-title {
  margin: 0;
  font-weight: 700;
  font-size: 1.25rem;
  color: #1e293b;
}

.custom-modal-body {
  padding: 1.5rem;
  flex-grow: 1;
}

.custom-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #f1f3f5;
  background-color: #f8fafc;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.custom-select {
  border-radius: 8px;
  padding: 0.625rem 0.875rem;
  font-size: 0.95rem;
  border: 1px solid #cbd5e1;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.custom-select:focus {
  border-color: #6366f1;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(99, 102, 241, 0.25);
}

.custom-btn-skip {
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
}

.custom-btn-skip:hover {
  background-color: #e2e8f0;
  color: #1e293b;
}

.custom-btn-select {
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  background-color: #6366f1;
  border: 1px solid #6366f1;
  color: #ffffff;
  transition: background-color 0.15s ease-in-out, opacity 0.15s ease-in-out;
}

.custom-btn-select:hover:not(:disabled) {
  background-color: #4f46e5;
  border-color: #4f46e5;
}

.custom-btn-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal Fade Transitions */
.custom-modal-fade-enter-active,
.custom-modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.custom-modal-fade-enter-from,
.custom-modal-fade-leave-to {
  opacity: 0;
}

.custom-modal-fade-enter-active .custom-modal-card {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.custom-modal-fade-leave-active .custom-modal-card {
  transition: transform 0.2s ease-in;
}

.custom-modal-fade-enter-from .custom-modal-card {
  transform: translateY(20px);
}

.custom-modal-fade-leave-to .custom-modal-card {
  transform: translateY(-20px);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.01); }
}

.animate-pulse:hover {
  animation: pulse 2s infinite ease-in-out;
}

.custom-lang-toggle {
  font-weight: 500;
  border-radius: 6px;
  padding: 0.25rem 0.625rem;
  font-size: 0.85rem;
  transition: all 0.15s ease-in-out;
}

.custom-lang-toggle i {
  font-size: 0.9rem;
}
</style>
